import { useState, useRef, useCallback, type Dispatch, type SetStateAction } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  uploadWebsites,
  refetchWebsite,
  waitForUploadJob,
  UPLOAD_JOB_UNKNOWN_ERROR,
} from '@/api/docsAPI'
import type { WebsiteUploadResult } from '@/models/Orgs'
import type { UUID } from 'crypto'

// ── Types ────────────────────────────────────────────────────

export type PageStatus = 'pending' | 'processing' | 'ingested' | 'error'

export interface PageEntry {
  url: string
  title?: string
  status: PageStatus
  error?: string
  docId?: number
  dateAdded?: string
}

interface BatchScrapeState {
  isRunning: boolean
  currentBatch: number
  totalBatches: number
}

// ── Helpers ──────────────────────────────────────────────────

function stripScheme(url: string): string {
  return url.replace(/^https?:\/\//, '')
}

function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size))
  }
  return chunks
}

/** Fold a job's per-website results into the page list.
 *
 * Exported so the step can apply the results of a job it adopted after losing
 * the watch, using the same mapping as the run that started it. */
export function applyResults(
  pages: PageEntry[],
  results: WebsiteUploadResult[]
): PageEntry[] {
  const resultMap = new Map(results.map((r) => [stripScheme(r.website), r]))
  return pages.map((p) => {
    const result = resultMap.get(stripScheme(p.url))
    if (!result) return p
    if (result.success) {
      const docId = result.doc_responses?.[0]?.id
      return {
        ...p,
        status: 'ingested' as const,
        error: undefined,
        docId,
        dateAdded: new Date().toLocaleDateString('en-NZ', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }),
      }
    }
    return { ...p, status: 'error' as const, error: result.error }
  })
}

function markUrls(
  pages: PageEntry[],
  urls: string[],
  status: PageStatus,
  error?: string
): PageEntry[] {
  const urlSet = new Set(urls)
  return pages.map((p) =>
    urlSet.has(p.url) ? { ...p, status, error } : p
  )
}

// ── Hook ─────────────────────────────────────────────────────

const BATCH_SIZE = 3

// Used when the request itself failed before a job existed, so there is nothing
// running to re-attach to. Distinct from a lost watch, which is not a failure.
const GENERIC_SCRAPE_ERROR = 'Could not start reading this page — please try again'

export function useBatchScrape(
  orgUuid: string,
  getToken: () => Promise<string>
) {
  const queryClient = useQueryClient()
  const [state, setState] = useState<BatchScrapeState>({
    isRunning: false,
    currentBatch: 0,
    totalBatches: 0,
  })
  const controllerRef = useRef<AbortController | null>(null)

  /** Returns true when every URL was ingested successfully. */
  const run = useCallback(
    async (
      urls: string[],
      setPages: Dispatch<SetStateAction<PageEntry[]>>,
      existingDocsByUrl?: Map<string, number>,
    ): Promise<boolean> => {
      if (urls.length === 0) return true
      const controller = new AbortController()
      controllerRef.current = controller
      const chunks = chunkArray(urls, BATCH_SIZE)
      setState({ isRunning: true, currentBatch: 0, totalBatches: chunks.length })
      let allOk = true

      for (let i = 0; i < chunks.length; i++) {
        if (controller.signal.aborted) { allOk = false; break }
        const chunk = chunks[i]
        setState((s) => ({ ...s, currentBatch: i + 1 }))
        setPages((prev) => markUrls(prev, chunk, 'processing'))

        try {
          const token = await getToken()
          const job = await uploadWebsites({
            token,
            websites: chunk,
            orgUuid: orgUuid as UUID,
            signal: controller.signal,
          })
          const results = await waitForUploadJob({
            token,
            jobId: job.job_id,
            signal: controller.signal,
          })

          // For "already uploaded" failures, refetch the existing doc instead.
          // Also check doc_responses from successful results in this same batch —
          // a multi-page crawl of the root domain can upload sub-pages that are
          // also listed as individual pending URLs, causing false duplicates.
          const batchUploadedByUrl = new Map<string, number>()
          for (const r of results) {
            if (r.success && r.doc_responses) {
              for (const doc of r.doc_responses) {
                if (doc.website_url) {
                  batchUploadedByUrl.set(stripScheme(doc.website_url), doc.id)
                }
              }
            }
          }

          const finalResults = await Promise.all(
            results.map(async (r) => {
              if (r.success || !r.error?.includes('already been uploaded')) return r
              const docId = existingDocsByUrl?.get(stripScheme(r.website))
                ?? batchUploadedByUrl.get(stripScheme(r.website))
              if (!docId || controller.signal.aborted) return r
              try {
                return await refetchWebsite({ token, docId })
              } catch {
                return r
              }
            })
          )

          setPages((prev) => applyResults(prev, finalResults))
          if (finalResults.some((r) => !r.success)) allOk = false
        } catch (err) {
          if (controller.signal.aborted) { allOk = false; break }

          // The upload is fire-and-forget: POST /websites returns 202 and the
          // crawl runs on the worker, so losing the watch says nothing about
          // whether the work succeeded. It usually does. Stamping every URL in
          // the chunk 'error' claimed a failure that had not happened — and the
          // catch spans getToken, uploadWebsites and waitForUploadJob, so a
          // token refresh, a 404 and a genuine crawl error all read the same.
          const watchLost =
            err instanceof Error && err.message === UPLOAD_JOB_UNKNOWN_ERROR
          if (watchLost) {
            // Leave the pages 'processing'. The step's adoption lookup re-attaches
            // to the running job and applies the real outcome when it lands.
            setState((s) => ({ ...s, isRunning: false }))
            return false
          }

          setPages((prev) =>
            markUrls(prev, chunk, 'error', GENERIC_SCRAPE_ERROR),
          )
          setState((s) => ({ ...s, isRunning: false }))
          return false // stop on network failure
        }
      }

      setState((s) => ({ ...s, isRunning: false }))
      if (!controller.signal.aborted) {
        queryClient.invalidateQueries({ queryKey: ['orgDocs', orgUuid] })
      }
      return allOk
    },
    [orgUuid, getToken, queryClient]
  )

  const abort = useCallback(() => {
    controllerRef.current?.abort()
  }, [])

  return { ...state, run, abort }
}
